import React from 'react';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import {
  DotNetValidationProblemDetailsTyped,
  MajorEntity,
  StudyLevelEntity,
  StudyYearEntity,
  UniversityEntity,
  VseApplicationEntity,
} from '../types';
import * as VseValidation from '../utils/validate';

export interface VseApplFormPersonalParticularsProps extends Pick<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  data: VseApplicationEntity,
  majorOptions: MajorEntity[],
  studyLevelOptions: StudyLevelEntity[],
  studyYearOptions: StudyYearEntity[],
  universityOptions: UniversityEntity[]
  onChange: (
    changes: Pick<Partial<VseApplicationEntity>,
      'familyName' |
      'firstName' |
      'middleName' |
      'homeUniversityID' |
      'homeUniversityStudentNumber' |
      'studyLevel' |
      'major' |
      'studyYear'>
  ) => void,
};

export function validatePersonalParticulars(valProblems: DotNetValidationProblemDetailsTyped<VseApplicationEntity>, data: VseApplicationEntity): DotNetValidationProblemDetailsTyped<VseApplicationEntity> | undefined {
  VseValidation.validateRequired(valProblems, 'familyName', 'Family Name', data.familyName);
  VseValidation.validateMaxLength(valProblems, 'familyName', 'Family Name', 50, data.familyName);

  VseValidation.validateRequired(valProblems, 'firstName', 'Given Name', data.firstName);
  VseValidation.validateMaxLength(valProblems, 'firstName', 'Given Name', 50, data.firstName);

  VseValidation.validateMaxLength(valProblems, 'middleName', 'Middle Name', 50, data.middleName);

  VseValidation.validateRequired(valProblems, 'homeUniversityStudentNumber', 'Home U Student ID Number', data.homeUniversityStudentNumber);
  VseValidation.validateMaxLength(valProblems, 'homeUniversityStudentNumber', 'Home U Student ID Number', 20, data.homeUniversityStudentNumber);

  VseValidation.validateRequired(valProblems, 'studyLevel', 'Current Study Level', data.studyLevel);
  VseValidation.validateRequired(valProblems, 'major', 'Major', data.major);
  VseValidation.validateRequired(valProblems, 'studyYear', 'Current Study Year', data.studyYear);

  return VseValidation.returnIfInvalid(valProblems);
}

export function VseApplFormPersonalParticulars(props: VseApplFormPersonalParticularsProps) {
  const { familyName, firstName, middleName, emailAddress, homeUniversityStudentNumber, studyYear } = props.data;
  const studyLevel = props.studyLevelOptions.length > 0 ? props.data.studyLevel : '';
  const homeUniversityID = props.universityOptions.length > 0 ? props.data.homeUniversityID : '';
  const major = props.majorOptions.length > 0 ? props.data.major : '';
  const homeUniv = props.universityOptions.find((value) => value.universityID === homeUniversityID);
  const universityName = homeUniv ? homeUniv.universityName : '';

  return (
    <Grid className={props.className} container>
      <Grid container>
        <Grid item xs={12} md={3}>
          <TextField
            label='Email'
            value={emailAddress}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <TextField
            label='Home University'
            value={universityName}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} md={3}>
          <TextField
            label='Family Name'
            required
            helperText='As shown on passport'
            value={familyName}
            onChange={
              (ev) => props.onChange({ familyName: ev.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label='Given Name'
            required
            helperText='As shown on passport'
            value={firstName}
            onChange={
              (ev) => props.onChange({ firstName: ev.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label='Middle Name'
            helperText='As shown on passport'
            value={middleName}
            onChange={
              (ev) => props.onChange({ middleName: ev.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label='Home U Student ID Number'
            required
            value={homeUniversityStudentNumber}
            onChange={
              (ev) => props.onChange({ homeUniversityStudentNumber: ev.target.value })
            }
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} md={3}>
          <TextField
            label="Current Study Level"
            select
            required
            value={studyLevel}
            onChange={
              (ev) => props.onChange({ studyLevel: ev.target.value })
            }
          >
            {props.studyLevelOptions.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.description}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label='Major'
            select
            required
            value={major}
            onChange={
              (ev) => props.onChange({ major: ev.target.value })
            }
          >
            {props.majorOptions.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.description}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Current Study Year"
            select
            required
            value={studyYear}
            onChange={
              (ev) => props.onChange({ studyYear: ev.target.value })
            }
          >
            {props.studyYearOptions.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.description}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Grid>
  );
}
